import {
  AemConfiguration,
  AUTH_STORAGE_EXPIRY,
  AUTH_STORAGE_KEY,
  EventEnum,
  HTTPStatusCode,
} from '@as-react/common-types';
import axios, { AxiosError, AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { EventUtil } from './EventUtil';
import { localStorageUtil } from './localStorage.util';

const axiosClient = axios.create({});

declare global {
  interface Window {
    SITE: { Config?: AemConfiguration; data?: Record<string, unknown> };
  }
}

axiosClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    switch (error.response?.status) {
      case HTTPStatusCode.Forbidden:
        EventUtil.dispatch(EventEnum.USER_SECURITY_LEVEL_TOO_LOW);
        break;
      default:
        return Promise.reject(error);
    }
  }
);

if (typeof window !== 'undefined' && window?.SITE?.Config?.feature_toggles.use_new_auth_flow) {
  axiosClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (config.withCredentials) {
        const authStorage = localStorageUtil.getWithExpiry(AUTH_STORAGE_KEY);
        if (authStorage?.access_token) {
          config.headers['Authorization'] = `Bearer ${authStorage.access_token}`;
        }
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  createAuthRefreshInterceptor(axiosClient, failedRequest => {
    const authStorage = localStorageUtil.getWithExpiry(AUTH_STORAGE_KEY);
    if (!authStorage) {
      return Promise.resolve();
    }
    const { refresh_token } = authStorage;
    const formdata = new FormData();

    formdata.append('refresh_token', refresh_token!);
    formdata.append('grant_type', 'refresh_token');
    formdata.append('client_id', window.SITE.Config!.oauth.clientId);

    return axiosClient
      .request({
        url: '/authorization-server/oauth2/token',
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: formdata,
      })
      .then(tokenRefreshResponse => {
        localStorageUtil.setWithExpiry(AUTH_STORAGE_KEY, tokenRefreshResponse.data, AUTH_STORAGE_EXPIRY);

        failedRequest.response.config.headers['Authorization'] = 'Bearer ' + tokenRefreshResponse.data.access_token;
        return Promise.resolve();
      });
  });
}

export { axiosClient };
